import { motion } from "framer-motion";
import Reveal from "@/components/7fcc/Reveal";
import SectionHeader from "@/components/7fcc/SectionHeader";
import Logo from "@/components/7fcc/Logo";
import OptInVsl, { useGateOpen } from "@/components/7fcc/OptInVsl";
import GhlSurvey from "@/components/7fcc/GhlSurvey";
import FAQAccordion from "@/components/7fcc/FAQAccordion";
import CTAButton from "@/components/7fcc/CTAButton";
import VideoTestimonials from "@/components/7fcc/VideoTestimonials";
import {
  BRAND,
  HERO,
  VSL,
  OPTIN,
  PROOF,
  VIDEO_TESTIMONIALS_CAPTION,
  TESTIMONIAL_VIDEOS,
  WINS_TOP,
  WINS_VIDEOS,
  SPOTLIGHT,
  CTA,
  QUIZ,
  SURVEY,
  FAQ,
  FOOTER,
} from "@/data/militaryContent";

const pick = (keys) => keys.map((key) => TESTIMONIAL_VIDEOS[key]);
import { EASE } from "@/lib/motion";

// ═══════════════════════════════════════════════════════════════════════
// /military campaign sections — exact mirrors of /ready, fed from
// src/data/militaryContent.js. Visual components are shared; content is
// fully isolated. All testids are prefixed "military-".
// ═══════════════════════════════════════════════════════════════════════

// Every section shares one background (bg-ink-950) per Joe's direction.
export function MilitaryHero() {
  return (
    <section
      className="relative flex flex-col items-center overflow-hidden bg-ink-950 px-6 pb-10 pt-14 text-center md:pb-12 md:pt-20"
      data-testid="military-hero-section"
    >
      <div
        aria-hidden="true"
        className="absolute left-1/2 top-[2%] h-[42vh] w-[70vw] max-w-[880px] -translate-x-1/2 bg-[radial-gradient(ellipse,rgba(91,138,60,0.18),transparent_65%)] blur-3xl"
      />
      <div className="relative flex max-w-5xl flex-col items-center">
        <h1 className="font-display text-[clamp(1.3rem,3.6vw,2.75rem)] font-semibold uppercase leading-[1.1] tracking-[-0.01em]">
          {HERO.headlineLines.map((line, i) => (
            <span key={line} className="block overflow-hidden pb-1">
              <motion.span
                className="text-chrome block"
                initial={{ y: "112%" }}
                animate={{ y: "0%" }}
                transition={{ duration: 1, delay: 0.15 + i * 0.13, ease: EASE }}
                data-testid={`military-hero-headline-${i + 1}`}
              >
                {line}
              </motion.span>
            </span>
          ))}
        </h1>
      </div>
    </section>
  );
}

export function MilitaryVslSection() {
  return (
    <section id="vsl" className="relative bg-ink-950 px-6 pb-16 pt-4 md:pb-24 md:pt-6" data-testid="military-vsl-section">
      <div className="mx-auto max-w-[900px]">
        <SectionHeader center title={VSL.headline} testId="military-vsl-heading" />
        <Reveal delay={0.15} className="mt-8 md:mt-10">
          <OptInVsl src={VSL.videoSrc} optin={OPTIN} survey={SURVEY} testId="military-vsl-video" />
        </Reveal>
      </div>
    </section>
  );
}

export function MilitaryQuizSection() {
  // While the video gate's survey is open, park this copy so the page never
  // has two of the same GHL survey iframe loaded at once.
  const gateOpen = useGateOpen();
  return (
    <section id="qualify" className="relative bg-ink-950 px-6 py-16 md:py-24" data-testid="military-quiz-section">
      <div
        aria-hidden="true"
        className="absolute left-1/2 top-1/2 h-[560px] w-[860px] max-w-full -translate-x-1/2 -translate-y-1/2 rounded-full bg-[radial-gradient(circle,rgba(91,138,60,0.07),transparent_65%)] blur-3xl"
      />
      <div className="relative mx-auto max-w-3xl">        <div className="relative rounded-2xl border border-glowblue bg-ink-850/80 p-6 shadow-panel backdrop-blur-sm md:p-10 md:pb-8">
          <div
            className="pointer-events-none absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-[rgba(91,138,60,0.6)] to-transparent"
            aria-hidden="true"
          />
          <SectionHeader center title={QUIZ.headline} testId="military-quiz-heading" />
          <Reveal delay={0.15} className="mt-4 md:mt-6">
            {gateOpen ? (
              <div className="h-[570px]" aria-hidden="true" />
            ) : (
              <GhlSurvey
                src={SURVEY.src}
                iframeId={SURVEY.iframeId}
                title={SURVEY.title}
                testId="military-ghl-survey"
              />
            )}
          </Reveal>
        </div>
      </div>

      <Reveal delay={0.2} className="relative mx-auto mt-12 w-full max-w-4xl md:mt-16">
        <div
          className="grid items-center gap-8 rounded-lg border border-line bg-ink-900/60 p-6 md:grid-cols-[1.2fr_1fr] md:p-8"
          data-testid="military-spotlight"
        >
          <div className="text-center md:text-left">
            <h3
              className="font-display text-2xl font-semibold leading-tight text-fg md:text-4xl"
              data-testid="military-spotlight-headline"
            >
              {SPOTLIGHT.headline}
            </h3>
            <p
              className="mt-4 text-sm leading-relaxed text-fg-muted md:text-lg"
              data-testid="military-spotlight-support"
            >
              {SPOTLIGHT.support}
            </p>
          </div>
          <div className="mx-auto w-full max-w-[300px]">
            <VideoTestimonials
              videos={[TESTIMONIAL_VIDEOS[SPOTLIGHT.videoKey]]}
              layout="single"
              testIdPrefix="military-spotlight-video"
            />
          </div>
        </div>
      </Reveal>
    </section>
  );
}

export function MilitaryProofSection() {
  return (
    <section className="bg-ink-950 px-6 py-16 md:py-24" data-testid="military-proof-section">
      <div className="mx-auto max-w-5xl">
        <SectionHeader
          center
          title={PROOF.headline}
          support={PROOF.support}
          testId="military-proof-heading"
        />

        <div
          className="mt-10 gap-5 [column-fill:_balance] sm:columns-2 md:columns-3 md:mt-12"
          data-testid="military-proof-wall"
        >
          {PROOF.items.map((item, i) => (
            <Reveal key={item.src} delay={i * 0.06} className="mb-5 break-inside-avoid">
              <figure
                className="overflow-hidden rounded-lg border border-line bg-ink-900"
                data-testid={`military-proof-item-${i + 1}`}
              >
                <img src={item.src} alt={item.alt} loading="lazy" className="w-full" />
              </figure>
            </Reveal>
          ))}
        </div>

      </div>
    </section>
  );
}

export function MilitaryVideoWinsSection() {
  return (
    <section className="bg-ink-950 px-6 py-16 md:py-24" data-testid="military-video-wins-section">
      <div className="mx-auto max-w-5xl">
        <Reveal>
          <p
            className="flex items-center justify-center gap-3 text-[13px] font-semibold uppercase tracking-[0.3em] text-brand-light"
            data-testid="military-video-wins-eyebrow"
          >
            <span className="h-px w-6 bg-brand/60" aria-hidden="true" />
            Wins In Their Own Words
            <span className="h-px w-6 bg-brand/60" aria-hidden="true" />
          </p>
        </Reveal>

        <div className="mt-8 md:mt-10">
          <VideoTestimonials
            videos={pick(WINS_TOP)}
            layout="pair"
            testIdPrefix="military-wins-top"
          />
        </div>
        <div className="mt-4 md:mt-6">
          <VideoTestimonials
            videos={pick(WINS_VIDEOS)}
            layout="five"
            testIdPrefix="military-video-testimonial"
          />
        </div>

        <Reveal delay={0.12}>
          <p
            className="mt-8 text-center text-sm leading-relaxed text-fg-muted md:mt-10"
            data-testid="military-video-wins-caption"
          >
            {VIDEO_TESTIMONIALS_CAPTION}
          </p>
        </Reveal>
      </div>
    </section>
  );
}

export function MilitaryFaqSection() {
  return (
    <section className="bg-ink-950 px-6 py-16 md:py-24" data-testid="military-faq-section">
      <div className="mx-auto max-w-3xl">
        <Reveal>
          <h2
            className="text-center font-display text-3xl font-semibold leading-[1.08] text-fg md:text-5xl"
            data-testid="military-faq-heading"
          >
            {FAQ.heading}
          </h2>
        </Reveal>
        <Reveal delay={0.12} className="mt-12">
          <FAQAccordion items={FAQ.items} />
        </Reveal>
      </div>
    </section>
  );
}

export function MilitaryBottomCta() {
  return (
    <section className="bg-ink-950 px-6 py-14 text-center md:py-16" data-testid="military-bottom-cta">
      <Reveal>
        <CTAButton label={CTA.label} targetId={CTA.targetId} testId="military-bottom-cta" />
      </Reveal>
    </section>
  );
}

export function MilitaryFooter() {
  return (
    <footer className="border-t border-line bg-ink-950 px-6 py-14" data-testid="military-footer">
      <div className="mx-auto flex max-w-6xl flex-col items-center gap-8 text-center">
        <Logo className="w-44 opacity-90" />
        <p className="font-display text-sm uppercase tracking-[0.28em] text-silver" data-testid="military-footer-brand">
          {BRAND.name}
        </p>
        <nav className="flex flex-wrap items-center justify-center gap-x-8 gap-y-3" aria-label="Legal">
          {FOOTER.links.map((link) => (
            <a
              key={link.label}
              href={link.href}
              data-testid={`military-footer-link-${link.label.toLowerCase().replace(/\s+/g, "-")}`}
              className="text-xs uppercase tracking-[0.2em] text-fg-dim transition-colors duration-300 hover:text-fg"
            >
              {link.label}
            </a>
          ))}
        </nav>
        <p className="text-xs text-fg-dim" data-testid="military-footer-copyright">
          {FOOTER.copyright}
        </p>
      </div>
    </footer>
  );
}
